import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FaMapMarkerAlt, FaUser, FaCalendarAlt, FaMapMarkedAlt, FaImage } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import TagSelector from '../common/TagSelector';
import useApi from '../../Api';
import { useAuth } from '../../AuthContext';
import { processImage } from '../../utils/ImageProcessor';
import usePopup from '../common/popup/usePopup';
import Popup from '../common/popup/Popup';
import { toast } from 'react-toastify';
import '../../styles/components/common/AdditionalInfo.css';

const AdditionalInfo = ({ data, enableTagSelector = false, onTagsChange }) => {
  const [tags, setTags] = useState(data.tags || []);
  const [thumbnailPreview, setThumbnailPreview] = useState(data.thumbnail_url || '');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const apiClient = useApi();
  const { token } = useAuth();
  const { isPopupVisible, showPopup, hidePopup } = usePopup();

  useEffect(() => {
    setTags(data.tags || []);
  }, [data.tags]);

  useEffect(() => {
    setThumbnailPreview(data.thumbnail_url || '');
  }, [data.thumbnail_url]);

  const handleTagsUpdate = (updatedTags) => {
    setTags(updatedTags);
    if (onTagsChange) onTagsChange(updatedTags);
  };

  const handleThumbnailButtonClick = () => {
    if (thumbnailPreview) {
      showPopup();
    } else {
      fileInputRef.current.click();
    }
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      try {
        const processedBlob = await processImage(file, 1280, 720, 1280, 720, 0.85, 10);
        const formData = new FormData();
        formData.append("thumbnail", processedBlob, `${data.itineraryId}_thumbnail.jpg`);
        const response = await apiClient.post(`/itineraries/${data.itineraryId}/thumbnail`, formData, {
          headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` }
        });
        const newUrl = response.data.thumbnail_url;
        const newUrlWithCacheBust = newUrl + (newUrl.includes('?') ? '&' : '?') + 'cache=' + Date.now();
        setThumbnailPreview(newUrlWithCacheBust);
      } catch (err) {
        toast.error("Thumbnail upload failed", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setUploading(false);
      }
    }
  };

  const handleThumbnailDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/itineraries/${data.itineraryId}/thumbnail`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThumbnailPreview('');
      hidePopup();
    } catch (err) {
      console.error("Thumbnail deletion failed", err);
      toast.error("Thumbnail deletion failed", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleEditThumbnail = () => {
    hidePopup();
    fileInputRef.current.click();
  };

  return (
    <div className="additional-info">
      <div className="info-labels">
        {data.destinationName && (
          <div className="info-item">
            <FaMapMarkerAlt className="info-icon" />
            <Link to={data.destinationLink} className="destination-link">{data.destinationName}</Link>
          </div>
        )}
        {data.authorName && (
          <div className="info-item">
            <FaUser className="info-icon" />
            <span>{data.authorName}</span>
          </div>
        )}
        {data.updatedDate && (
          <div className="info-item">
            <FaCalendarAlt className="info-icon" />
            <span>{data.updatedDate}</span>
          </div>
        )}
        {data.itineraryLink && (
          <div className="info-item">
            <FaMapMarkedAlt className="info-icon" />
            <Link to={data.itineraryLink} className="destination-link">View Itinerary</Link>
          </div>
        )}
        {!data.hideThumbnail && data.itineraryId && (
          <div className="info-item">
            <FaImage className="info-icon" />
            <a href="#" className="destination-link thumbnail-link" onClick={(e) => { e.preventDefault(); if (!uploading && !deleting) { handleThumbnailButtonClick(); } }}>
              {uploading ? "Uploading..." : deleting ? "Deleting..." : thumbnailPreview ? "View Thumbnail" : "Upload Thumbnail"}
            </a>
            <input type="file" accept="image/*" ref={fileInputRef} style={{ display: 'none' }} onChange={handleThumbnailChange} />
          </div>
        )}
      </div>
      {(enableTagSelector || (data.tags && data.tags.length > 0)) && (
        <div className="info-tags">
          {enableTagSelector ? (
            <TagSelector selectedTags={tags} setSelectedTags={handleTagsUpdate} />
          ) : (
            <div className="selected-tags">
              {data.tags.map((tag, index) => (
                <span key={index} className="tag-chip">{tag}</span>
              ))}
            </div>
          )}
        </div>
      )}
      {isPopupVisible && (
        <Popup
          title="Thumbnail"
          onCancel={hidePopup}
          footer={
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
              <button onClick={handleEditThumbnail} className="additional-info-popup-btn popup-btn-edit">Edit Thumbnail</button>
              <button onClick={handleThumbnailDelete} disabled={deleting} className="additional-info-popup-btn popup-btn-delete">
                {deleting ? "Deleting..." : "Delete Thumbnail"}
              </button>
            </div>
          }
        >
          <div style={{ textAlign: 'center' }}>
            {thumbnailPreview && <img src={thumbnailPreview} alt="Thumbnail" style={{ width: '100%', marginBottom: '16px' }} />}
          </div>
        </Popup>
      )}
    </div>
  );
};

AdditionalInfo.propTypes = {
  data: PropTypes.shape({
    destinationName: PropTypes.string,
    destinationLink: PropTypes.string,
    authorName: PropTypes.string,
    updatedDate: PropTypes.string,
    itineraryLink: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    thumbnail_url: PropTypes.string,
    itineraryId: PropTypes.string,
    hideThumbnail: PropTypes.bool,
  }).isRequired,
  enableTagSelector: PropTypes.bool,
  onTagsChange: PropTypes.func,
};

export default AdditionalInfo;
